/**
 * Full settlement (D03 §2 flow 3; old `handleSettleDebt` App.tsx:17820-17936): the account goes to zero in IQD and USD.
 * One operation per non-zero line — debt → `customer_deposit` (repayment), trust → `customer_withdraw` — IQD through
 * the chosen box, USD through the dollar vault (D-029; the old single box for both currencies, R-11, is gone), all
 * previewed first and committed in **one** transaction under one PIN (`commit-batch`, D-246 (c)).
 */
import { useState } from "react";
import { CustomerSettleModal } from "@faylasuf/ui";
import type { CustomerAccountRow, PreviewResult } from "../../api/types.js";
import { useApp } from "../../app/context.js";
import { errorMessage } from "../../app/errors.js";
import { useGateway } from "../../app/operations.js";
import { useToast } from "../../app/toast.js";
import type { CustomerBoxes } from "./boxes.js";
import { balancesLine, fmt, isZero } from "./format.js";

export function SettleContainer({ row, boxes, usdHidden, onClose }: { row: CustomerAccountRow; boxes: CustomerBoxes; usdHidden: boolean; onClose: () => void }) {
  const { t } = useApp();
  const toast = useToast();
  const gateway = useGateway();
  const [boxId, setBoxId] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const type = row.kind === "debt" ? "customer_deposit" : "customer_withdraw";
  const hasIqd = !isZero(row.iqd.balance);
  const hasUsd = !isZero(row.usd.balance);
  const canConfirm = (hasIqd || hasUsd) && (!hasIqd || boxId !== "") && !busy;

  const confirm = async () => {
    setBusy(true);
    try {
      const extra = note.trim() ? { note: note.trim() } : {};
      const previews: PreviewResult[] = [];
      try {
        if (hasIqd) previews.push(await gateway.preview(type, { accountId: row.iqd.accountId, amount: row.iqd.balance.replace(/^-/, ""), boxAccountId: boxId, ...extra }));
        if (hasUsd) previews.push(await gateway.preview(type, { accountId: row.usd.accountId, amount: row.usd.balance.replace(/^-/, ""), boxAccountId: boxes.dollarVaultId, ...extra }));
      } catch (e) {
        toast(errorMessage(t, e), "error");
        return;
      }
      const res = await gateway.commitBatch(previews, { pinMessage: t("cust.pin_settle", { name: row.name }) });
      if (res) onClose();
    } catch {
      /* toasted by the gateway */
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <CustomerSettleModal
        name={row.name}
        balancesLabel={balancesLine(t, row, usdHidden)}
        boxValue={boxId}
        onBoxChange={setBoxId}
        boxOptions={boxes.iqd}
        usdNote={hasUsd && !usdHidden ? t("cust.settle_usd_note", { amount: fmt(row.usd.balance) }) : null}
        note={note}
        onNoteChange={setNote}
        canConfirm={canConfirm}
        onConfirm={() => void confirm()}
        onClose={onClose}
      />
      {gateway.pin.dialog}
    </>
  );
}
